import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { AvatarConfig } from '@/lib/avatar-config';

interface AvatarDisplayProps {
  username?: string | null;
  avatarConfig?: AvatarConfig | null;
  avatarUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showAnimation?: boolean;
  animationType?: 'idle' | 'celebration' | 'thinking';
  className?: string;
  // Cosmetics
  accessory?: { type: string; color?: string } | null;
  frame?: { type: string; color?: string } | null;
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-16 w-16',
  xl: 'h-24 w-24'
};

const accessoryEmojis: Record<string, string> = {
  crown: '👑',
  hat: '🎩',
  glasses: '🕶️',
  headphones: '🎧',
  bow: '🎀',
  halo: '😇',
  horns: '😈',
  star: '⭐',
};

const frameColors: Record<string, string> = {
  bronze: 'ring-amber-600',
  silver: 'ring-slate-400',
  gold: 'ring-yellow-400',
  diamond: 'ring-cyan-400',
  rainbow: 'ring-purple-500',
};

export const AvatarDisplay = ({
  username,
  avatarConfig,
  avatarUrl,
  size = 'md',
  showAnimation = false,
  animationType = 'idle',
  className,
  accessory,
  frame
}: AvatarDisplayProps) => {
  // Generate avatar based on config or use uploaded image
  const getAvatarContent = () => {
    if (avatarUrl) {
      return <AvatarImage src={avatarUrl} alt={username || 'Avatar'} />;
    }

    if (avatarConfig && avatarConfig.colors && avatarConfig.category && avatarConfig.style) {
      // Create animated avatar based on config
      const { category, style, colors, animations } = avatarConfig;
      const animation = showAnimation ? animations?.[animationType] : 'none';
      
      return (
        <div 
          className={cn(
            'w-full h-full rounded-full flex items-center justify-center text-white font-bold overflow-hidden',
            showAnimation && 'animate-float'
          )}
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            boxShadow: showAnimation ? `0 0 20px ${colors.accent}40` : undefined
          }}
        >
          {getAvatarEmoji(category, style)}
        </div>
      );
    }

    // Fallback to initials
    const initials = username?.[0]?.toUpperCase() || 'U';
    return (
      <AvatarFallback className="bg-teen-blue text-white font-semibold">
        {initials}
      </AvatarFallback>
    );
  };

  // Get frame class based on equipped cosmetic
  const frameClass = frame?.type ? frameColors[frame.type] || '' : '';
  const hasFrame = frame?.type && frameColors[frame.type];

  return (
    <div className="relative inline-block">
      <Avatar className={cn(
        sizeClasses[size], 
        className,
        hasFrame && `ring-2 ${frameClass} ring-offset-2 ring-offset-background`
      )}>
        {getAvatarContent()}
      </Avatar>
      
      {/* Accessory overlay */}
      {accessory?.type && accessoryEmojis[accessory.type] && (
        <span 
          className={cn(
            "absolute -top-1 -right-1 text-sm",
            size === 'sm' && "text-xs -top-0.5 -right-0.5",
            size === 'lg' && "text-lg -top-1.5 -right-1.5",
            size === 'xl' && "text-xl -top-2 -right-2"
          )}
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
        >
          {accessoryEmojis[accessory.type]}
        </span>
      )}
    </div>
  );
};

// Helper function to get emoji based on category and style
function getAvatarEmoji(category: string, style: string): string {
  const emojiMap: Record<string, Record<string, string>> = {
    'human-male': {
      casual: '😎',
      sporty: '🏃‍♂️', 
      formal: '👨‍💼',
      gamer: '🎮',
      artist: '🎨'
    },
    'human-female': {
      casual: '😊',
      sporty: '🏃‍♀️',
      formal: '👩‍💼', 
      gamer: '🎮',
      artist: '🎨'
    },
    animal: {
      cat: '🐱',
      dog: '🐶',
      fox: '🦊',
      panda: '🐼',
      wolf: '🐺',
      tiger: '🐯',
      rabbit: '🐰',
      owl: '🦉'
    },
    object: {
      robot: '🤖',
      alien: '👽',
      gem: '💎',
      crown: '👑',
      shield: '🛡️',
      lightning: '⚡',
      star: '⭐',
      crystal: '🔮'
    },
    fantasy: {
      wizard: '🧙‍♂️',
      knight: '⚔️',
      dragon: '🐉',
      unicorn: '🦄',
      phoenix: '🔥',
      mage: '🧙‍♀️',
      archer: '🏹',
      warrior: '⚔️'
    }
  };

  return emojiMap[category]?.[style] || '😊';
}